import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, PageState, TextField } from '../components/ui';
import { ApiClientError } from '../api/client';
import { fieldErrorsFromApiError } from '../lib/field-errors';
import { useAcceptInvite } from '../api/users';
import { useAuth } from './AuthContext';

function validate(password: string, confirm: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (password.length < 10) errors.password = 'Password must be at least 10 characters.';
  if (confirm !== password) errors.confirm = 'Passwords do not match.';
  return errors;
}

/**
 * Truly public (like /reset-password): the emailed invite link may be
 * opened with no existing session, or from a browser signed into a
 * different account entirely.
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const acceptInvite = useAcceptInvite();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="ne-auth">
        <Card className="ne-auth__card">
          <PageState
            kind="error"
            title="Invalid link"
            message="This invite link is missing its token. Ask whoever invited you to resend it."
          />
          <p className="ne-auth__foot">
            <Link to="/login">Back to sign in</Link>
          </p>
        </Card>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate(password, confirm);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const session = await acceptInvite.mutateAsync({ token: token!, password });
      applySession(session);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'AUTH_TOKEN_INVALID') {
        setError(
          'This invite link is invalid or has expired. Ask whoever invited you to resend it.',
        );
      } else if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
        } else {
          setError(err.message);
        }
      } else {
        setError(
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not accept the invite. Please try again.',
        );
      }
    }
  }

  return (
    <div className="ne-auth">
      <Card className="ne-auth__card">
        <h1 className="ne-auth__title">Join your team</h1>
        <p className="ne-auth__subtitle">Choose a password to finish setting up your account.</p>
        <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setFieldErrors((f) => {
                const { password: _p, ...rest } = f;
                return rest;
              });
            }}
            error={fieldErrors.password}
          />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setFieldErrors((f) => {
                const { confirm: _c, ...rest } = f;
                return rest;
              });
            }}
            error={fieldErrors.confirm}
          />
          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={acceptInvite.isPending}>
            {acceptInvite.isPending ? 'Joining…' : 'Join workspace'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
